// Light/dark theme toggle. The CSS side is already wired in
// public/index.html — `html.dark` triggers ~40 override rules.
// This component is the switch: it reads/writes localStorage
// ('theme' key: 'light' | 'dark' | 'system') and flips the
// classList on <html>.
//
// The FOUC-prevention script in index.html applies the persisted
// theme BEFORE React mounts, so first paint is already correct.
// This component just handles subsequent user toggles.

import React from 'react';

const LS_KEY = 'theme';

function readCurrent() {
  try {
    const t = localStorage.getItem(LS_KEY);
    if (t === 'light' || t === 'dark' || t === 'system') return t;
  } catch { /* private mode */ }
  return 'system';
}

function isDarkNow(pref) {
  if (pref === 'dark')  return true;
  if (pref === 'light') return false;
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch { return false; }
}

function apply(pref) {
  const dark = isDarkNow(pref);
  const el = document.documentElement;
  if (dark) el.classList.add('dark');
  else      el.classList.remove('dark');
}

export default function ThemeToggle() {
  const [pref, setPref] = React.useState(readCurrent);

  // Keep the class in sync when preference changes.
  React.useEffect(() => { apply(pref); }, [pref]);

  // When pref === 'system', follow OS changes live.
  React.useEffect(() => {
    if (pref !== 'system') return;
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (!mq) return;
    const listener = () => apply('system');
    mq.addEventListener?.('change', listener);
    return () => mq.removeEventListener?.('change', listener);
  }, [pref]);

  const setAndPersist = (next) => {
    setPref(next);
    try { localStorage.setItem(LS_KEY, next); } catch { /* ignore */ }
  };

  // Two-click cycle: light → dark → system → light.
  const cycle = () => {
    const next = pref === 'light' ? 'dark' : pref === 'dark' ? 'system' : 'light';
    setAndPersist(next);
  };

  const label   = pref === 'dark' ? 'Dark' : pref === 'light' ? 'Light' : 'System';
  const glyph   = pref === 'dark' ? '🌙'    : pref === 'light' ? '☀️'    : '🖥️';
  const nextHint = pref === 'light' ? 'dark' : pref === 'dark' ? 'system' : 'light';

  return (
    <button
      onClick={cycle}
      title={`Theme: ${label}. Click for ${nextHint}.`}
      aria-label={`Theme: ${label}. Click to switch to ${nextHint}.`}
      className="fixed bottom-4 right-24 z-[55] bg-slate-800 hover:bg-slate-700 text-white rounded-full w-11 h-11 flex items-center justify-center shadow-lg border border-slate-700 transition"
    >
      <span className="text-lg leading-none" aria-hidden="true">{glyph}</span>
    </button>
  );
}
